/**
 * Reusable conflict resolution dialog for optimistic locking.
 *
 * Displays when a version conflict is detected and provides users with
 * clear options to resolve the conflict.
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, RefreshCw, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FieldDiffViewer, FieldDiff } from "./field-diff-viewer";
import { useState } from "react";

interface ConflictResolutionDialogProps {
  /** Whether the dialog is open */
  open: boolean;

  /** Callback to change open state */
  onOpenChange: (open: boolean) => void;

  /** Current version the user has */
  currentVersion: number;

  /** Latest version on the server */
  latestVersion: number | null;

  /** Name of the resource (e.g., "Source", "Event", "Note") - localized */
  resourceName: string;

  /** Callback when user chooses to refresh */
  onRefresh: () => void | Promise<void>;

  /** Callback when user chooses to overwrite */
  onOverwrite: () => void | Promise<void>;

  /** Whether an action is currently loading */
  loading?: boolean;

  /** Optional: Hide the overwrite option */
  hideOverwrite?: boolean;

  /** Optional: Field differences to display */
  fieldDiffs?: FieldDiff[];

  /** Optional: Custom field labels for the diff viewer */
  fieldLabels?: Record<string, string>;

  /** Optional: Custom field icons for the diff viewer */
  fieldIcons?: Record<string, React.ReactNode>;

  /** Localized text props */
  text?: {
    title?: string;
    message?: string;
    yourVersion?: string;
    currentVersion?: string;
    optionsTitle?: string;
    refreshOption?: string;
    overwriteOption?: string;
    cancelOption?: string;
    refreshButton?: string;
    overwriteButton?: string;
    cancelButton?: string;
    showFieldDiffs?: string;
    hideFieldDiffs?: string;
  };
}

export function ConflictResolutionDialog({
  open,
  onOpenChange,
  currentVersion,
  latestVersion,
  resourceName,
  onRefresh,
  onOverwrite,
  loading = false,
  hideOverwrite = false,
  fieldDiffs,
  fieldLabels,
  fieldIcons,
  text = {},
}: ConflictResolutionDialogProps) {
  const { t } = useTranslation();
  const [showFieldDiffs, setShowFieldDiffs] = useState(false);

  const defaultText = {
    title: text.title || t("common.title.conflict_detected"),
    message:
      text.message ||
      t("common.statictext.resource_modified_by_another", {
        resource: resourceName.toLowerCase(),
      }),
    yourVersion: text.yourVersion || t("common.label.your_version"),
    currentVersion: text.currentVersion || t("common.label.current_version"),
    optionsTitle: text.optionsTitle || t("common.statictext.conflict_options"),
    refreshOption:
      text.refreshOption || t("common.statictext.conflict_refresh_option"),
    overwriteOption:
      text.overwriteOption || t("common.statictext.conflict_overwrite_option"),
    cancelOption:
      text.cancelOption || t("common.statictext.conflict_cancel_option"),
    refreshButton:
      text.refreshButton || t("common.button.refresh_discard_changes"),
    overwriteButton:
      text.overwriteButton || t("common.button.overwrite_with_my_changes"),
    cancelButton: text.cancelButton || t("common.button.cancel"),
    showFieldDiffs:
      text.showFieldDiffs || t("common.button.show_field_differences"),
    hideFieldDiffs:
      text.hideFieldDiffs || t("common.button.hide_field_differences"),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-yellow-600">
            <AlertCircle className="h-5 w-5" />
            {defaultText.title}
          </DialogTitle>
          <DialogDescription className="space-y-3 pt-2">
            <p className="font-semibold">{defaultText.message}</p>
            <p className="text-sm">
              {defaultText.yourVersion}:{" "}
              <code className="font-mono bg-muted px-1 rounded">
                {currentVersion}
              </code>
              {" | "}
              {defaultText.currentVersion}:{" "}
              <code className="font-mono bg-muted px-1 rounded">
                {latestVersion ?? "?"}
              </code>
            </p>

            {/* Field Diff Toggle */}
            {fieldDiffs && fieldDiffs.length > 0 && (
              <div className="flex items-center justify-between">
                <div className="bg-muted p-3 rounded-md flex-1">
                  <p className="font-semibold mb-2 text-sm">
                    {defaultText.optionsTitle}:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>{defaultText.refreshOption}</li>
                    {!hideOverwrite && <li>{defaultText.overwriteOption}</li>}
                    <li>{defaultText.cancelOption}</li>
                  </ul>
                </div>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFieldDiffs(!showFieldDiffs)}
              className="flex items-center gap-2"
            >
              {showFieldDiffs ? (
                <>
                  <EyeOff className="h-4 w-4" />
                  {defaultText.hideFieldDiffs}
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4" />
                  {defaultText.showFieldDiffs}
                </>
              )}
            </Button>

            {/* Fallback for when no field diffs are available */}
            {(!fieldDiffs || fieldDiffs.length === 0) && (
              <div className="bg-muted p-3 rounded-md">
                <p className="font-semibold mb-2 text-sm">
                  {defaultText.optionsTitle}:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>{defaultText.refreshOption}</li>
                  {!hideOverwrite && <li>{defaultText.overwriteOption}</li>}
                  <li>{defaultText.cancelOption}</li>
                </ul>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Field Diff Viewer */}
        {showFieldDiffs && fieldDiffs && fieldDiffs.length > 0 && (
          <div className="mt-4">
            <FieldDiffViewer
              diffs={fieldDiffs}
              fieldLabels={fieldLabels}
              fieldIcons={fieldIcons}
              showOnlyChanged={true}
              loading={loading}
            />
          </div>
        )}

        <DialogFooter className="flex flex-col gap-2 sm:flex-col sm:justify-center sm:space-x-0">
          <Button
            onClick={onRefresh}
            variant="default"
            className="w-full"
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {defaultText.refreshButton}
          </Button>

          {!hideOverwrite && (
            <Button
              onClick={onOverwrite}
              variant="destructive"
              className="w-full"
              disabled={loading}
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              {defaultText.overwriteButton}
            </Button>
          )}

          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="w-full"
            disabled={loading}
          >
            {defaultText.cancelButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
